import React from "react";
import PropTypes from "prop-types";
import { CheckIcon } from '@heroicons/react/24/solid';
import { Checkbox, Radio } from 'components/ui';

/**
 * Formats raw field values into readable strings for display.
 */
export const formatApplicationFieldValue = (field, rawValue) => {
  if (rawValue === undefined || rawValue === null || rawValue === "") return "";

  if (Array.isArray(rawValue)) return rawValue.length ? rawValue.join(", ") : "";

  const normalized = typeof rawValue === "string" ? rawValue.trim() : rawValue;

  // FieldTypeId 5 = Checkbox
  if (field.FieldTypeId === 5) {
    if (typeof normalized === "string") {
      const lowerValue = normalized.toLowerCase();
      return ["true", "yes", "1", "on"].includes(lowerValue) ? "Yes" : "No";
    }
    return normalized ? "Yes" : "No";
  }

  if (typeof normalized === "boolean") return normalized ? "Yes" : "No";
  if (typeof normalized === "number")
    return Number.isNaN(normalized) ? "" : normalized.toString();

  return normalized.toString().length ? normalized.toString() : "";
};

const LABEL_WIDTH = "w-48 max-w-[200px]";

/**
 * ApplicationFormTemplate
 * - Displays form fields read-only with labels on left and "boxed" values on the right.
 */
const ApplicationFormTemplate = ({ applicationForm, formData, companyName }) => {
  const steps = applicationForm?.Steps || [];

  if (!steps.length) {
    return (
      <div className="mx-auto max-w-4xl bg-white p-8 text-center text-sm text-gray-600">
        No form fields available.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl bg-white p-12">
      {/* HEADER */}
      <div className="mb-10 text-start">
        <h1 className="mb-2 text-[26px] leading-tight font-bold uppercase  text-black">
          {companyName}
        </h1>
        <h2 className="mb-2 text-[22px] font-semibold tracking-wide text-black">
          {applicationForm?.Title}
        </h2>
        <h3 className="text-[16px] font-semibold tracking-wide text-gray-500">
          {applicationForm?.Description}
        </h3>
      </div>

      {/* FORM BODY */}
      <div className="space-y-12">
        {steps.map((step, sIdx) => {
          const fields = step.Fields || [];
          if (!fields.length) return null;

          return (
            <section key={sIdx} className="space-y-8 break-inside-avoid" style={{ pageBreakInside: 'avoid' }}>
              {step.StepTitle && (
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-black uppercase border-b border-gray-200 pb-1">
                    {step.StepTitle}
                  </h3>
                  {step.StepDescription && (
                    <p className="text-sm text-gray-500 mt-1">{step.StepDescription}</p>
                  )}
                </div>
              )}
              {fields.map((field) => {
                const displayValue = formatApplicationFieldValue(
                  field,
                  formData?.[field.FieldId]
                );

                const isTextArea =
                  field.FieldTypeId === 4 ||
                  /address|notes|remark|description/i.test(field.FieldLabel || "");

                // Skip inline fields here — they're rendered separately
                if (field.InlineGroup) return null;

                // ---------------------------
                // CHECKBOX GROUP (multiple options)
                // ---------------------------
                if (field.FieldTypeId === 5 && Array.isArray(field.Options) && field.Options.length) {
                  const rawValue = formData?.[field.FieldId];
                  const selectedValues = Array.isArray(rawValue)
                    ? rawValue.map((value) => String(value).trim())
                    : String(rawValue || "").split(",").map((value) => value.trim()).filter(Boolean);
                  
                  return (
                    <div key={field.FieldId} className="flex gap-4 items-start break-inside-avoid" style={{ pageBreakInside: 'avoid' }}>
                      <label className={`${LABEL_WIDTH} text-sm font-medium text-black pt-1 break-words`}>
                        {field.FieldLabel}
                      </label>

                      <div className="flex flex-col gap-3 items-start">
                        {field.Options.map((option, idx) => {
                          const optionValue =
                            typeof option === "object" ? option.OptionValue : option;
                          const optionLabel =
                            typeof option === "object" ? option.OptionLabel : option;
                          const isChecked = selectedValues.includes(optionValue);

                          return (
                            <label key={idx} className="flex items-center gap-2">
                              <Checkbox
                                checked={isChecked}
                                readOnly
                                color="primary"
                                className="pointer-events-none"
                              />
                              <span className="text-sm text-black break-words">{optionLabel}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // ---------------------------
                // SINGLE CHECKBOX (legacy fallback)
                // ---------------------------
                if (field.FieldTypeId === 5) {
                  const isChecked = displayValue?.toLowerCase() === "yes";

                  return (
                    <div
                      key={field.FieldId}
                      className="flex items-center gap-4 break-inside-avoid"
                      style={{ pageBreakInside: 'avoid' }}
                    >
                      <label className={`${LABEL_WIDTH} text-sm font-medium text-black`}>
                        {field.FieldLabel}
                      </label>

                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isChecked}
                          readOnly
                          color="primary"
                          className="pointer-events-none"
                        />
                        <span className="text-sm text-black">{isChecked ? "Yes" : "No"}</span>
                      </div>
                    </div>
                  );
                }

                // ---------------------------
                // RADIO (Style A: solid filled circle when selected)
                // ---------------------------
                if (
                  field.FieldTypeId === 6 &&
                  Array.isArray(field.Options) &&
                  field.Options.length
                ) {
                  const currentValue = formData?.[field.FieldId];

                  return (
                    <div key={field.FieldId} className="flex gap-4 items-start break-inside-avoid" style={{ pageBreakInside: 'avoid' }}>
                      <label className={`${LABEL_WIDTH} text-sm font-medium text-black pt-1`}>
                        {field.FieldLabel}
                      </label>

                      <div className="flex flex-wrap gap-5 items-center">
                        {field.Options.map((option, idx) => {
                          const optionValue =
                            typeof option === "object" ? option.OptionValue : option;
                          const optionLabel =
                            typeof option === "object" ? option.OptionLabel : option;

                          const isSelected = currentValue === optionValue;

                          return (
                            <label key={idx} className="flex items-center gap-2">
                              <Radio
                                checked={isSelected}
                                readOnly
                                color="primary"
                                className="pointer-events-none"
                              />
                              <span className="text-sm text-black">{optionLabel}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // ---------------------------
                // TEXT / TEXTAREA (side-by-side box)
                // ---------------------------
                return (
                  <div key={field.FieldId} className="flex items-start gap-4 break-inside-avoid" style={{ pageBreakInside: 'avoid' }}>
                    <label className={`${LABEL_WIDTH} text-sm font-medium text-black pt-2`}>
                      {field.FieldLabel}
                    </label>

                    <div
                      className={`
                        flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-black
                        ${isTextArea ? "min-h-[80px] whitespace-pre-line" : ""}
                      `}
                    >
                      {displayValue || <span className="text-gray-400 text-transparent">—</span>}
                    </div>
                  </div>
                );
              })}

              {/* ---------------------------
                  INLINE GROUPS (City / State / Zip)
                  Render under the same label column
              --------------------------- */}
              {step.InlineGroups?.map((group, gIdx) => (
                <div key={gIdx} className="flex gap-4 items-start break-inside-avoid" style={{ pageBreakInside: 'avoid' }}>
                  <label className={`${LABEL_WIDTH} text-sm font-medium text-black pt-2`}>
                    {/* If you want a label for the group, place here */}
                  </label>

                  <div className="flex-1 grid grid-cols-3 gap-6">
                    {group.map((fieldId) => {
                      const field = fields.find((f) => f.FieldId === fieldId);
                      if (!field) return null;

                      const displayValue = formatApplicationFieldValue(
                        field,
                        formData?.[field.FieldId]
                      );

                      return (
                        <div key={field.FieldId} className="space-y-1">
                          <label className="text-sm font-medium text-black">
                            {field.FieldLabel}
                          </label>

                          <div className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-black">
                            {displayValue || <span className="text-gray-400">—</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
};

ApplicationFormTemplate.propTypes = {
  applicationForm: PropTypes.shape({
    Steps: PropTypes.arrayOf(
      PropTypes.shape({
        StepId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        StepTitle: PropTypes.string,
        StepDescription: PropTypes.string,
        Fields: PropTypes.arrayOf(
          PropTypes.shape({
            FieldId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            FieldLabel: PropTypes.string.isRequired,
            IsRequired: PropTypes.bool,
            FieldTypeId: PropTypes.number,
            Options: PropTypes.array,
            InlineGroup: PropTypes.bool,
          })
        ),
        InlineGroups: PropTypes.arrayOf(PropTypes.array),
      })
    ),
    Title: PropTypes.string,
    HeaderFields: PropTypes.array,
  }),
  formData: PropTypes.object,
  companyName: PropTypes.string,
};

ApplicationFormTemplate.defaultProps = {
  applicationForm: null,
  formData: {},
  companyName: "",
};

export default ApplicationFormTemplate;
